import { Fragment, useMemo } from 'react';

import {
  FilterAppliedProps,
  FilterComparer,
  FilterConfig,
  FilterMenuProps,
  Filters,
  useCurrentFilter,
} from '@components/filters';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconRatingGood } from '@components/icons/rating-good';
import { IconRatingMedium } from '@components/icons/rating-medium';

import { useAuthenticatedUser } from 'features/authentication';
import { sortBy } from 'helpers/sorting';
import { useStore } from 'models/Provider';
import { IJob } from 'models/types';

import { FilterActionButtons } from './filter-action-buttons';
import { FilterComparerMenu } from './filter-comparer-menu';

type ExtraProps = {
  users: Array<{ value: string; label: string }>;
};

const RATING_COMPARERS: Array<FilterComparer> = [
  'is',
  'not_is',
  'any_of',
  'none_of',
  'is_rated_by',
  'not_is_rated_by',
];

function FilterApplicationRatingApplied({ data }: FilterAppliedProps<ExtraProps>) {
  const { t, userStore } = useStore();

  let value = '';

  if (data.comparer === 'is_rated_by' || data.comparer === 'not_is_rated_by') {
    const user = userStore.items.get(data.value);
    value = user ? user.name : '?';
  } else {
    if (Array.isArray(data.value)) {
      value = data.value.map((value) => t('applicants.filters.rating.' + value)).join(', ');
    } else {
      value = t('applicants.filters.rating.' + data.value);
    }
  }

  return (
    <Filters.AppliedFilter
      icon={<IconRatingGood />}
      category={t('applicants.filters.rating.title')}
      value={value}
      comparer={{
        value: data.comparer!,
        label: t('comparers.' + data.comparer).replace('...', ''),
      }}
    />
  );
}

function FilterApplicationRatingMenu({ users }: FilterMenuProps<ExtraProps>) {
  const { t } = useStore();
  const { state, actions, isMultiComparer, hasValue } = useCurrentFilter();
  const me = useAuthenticatedUser();

  const ratingItems = ['S', 'Y', 'N', 'D', '-'].map((rating) => ({
    label: t('applicants.filters.rating.' + rating),
    value: rating,
  }));

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('applicants.filters.rating.title')}
        icon={<IconArrowLeft />}
        onClick={actions.unselectCategory}
      />

      {state.comparer && (
        <Filters.ListItem
          heading
          label={t('comparers.' + state.comparer).replace('...', '')}
          icon={<IconArrowLeft />}
          onClick={actions.unselectComparer}
        />
      )}

      <Filters.ListSeparator />

      {!state.comparer && (
        <Fragment>
          <Filters.ListItem
            icon={<IconCheckmark color="green200" />}
            label={t('applicants.filters.rating.withmine')}
            onClick={() => {
              actions.applyFilter({ comparer: 'is_rated_by', value: String(me.id) });
            }}
          />

          <Filters.ListItem
            icon={<IconRatingMedium color="orange200" />}
            label={t('applicants.filters.rating.withoutmine')}
            onClick={() => {
              actions.applyFilter({ comparer: 'not_is_rated_by', value: String(me.id) });
            }}
          />

          <Filters.ListItem
            icon={<IconCloseRegular color="red200" />}
            label={t('applicants.filters.rating.-')}
            onClick={() => {
              actions.applyFilter({ comparer: 'is', value: '-' });
            }}
          />

          <Filters.ListSeparator />

          <FilterComparerMenu
            comparers={RATING_COMPARERS}
            onComparerSelect={actions.selectComparer}
          />
        </Fragment>
      )}
      {state.comparer && (
        <Fragment>
          <Filters.FilterValueMenu
            items={
              state.comparer === 'is_rated_by' || state.comparer === 'not_is_rated_by'
                ? users
                : ratingItems
            }
            isMulti={isMultiComparer}
            onSetValue={actions.selectValue}
            state={state}
          />
          <FilterActionButtons disableApply={!hasValue} />
        </Fragment>
      )}
    </Filters.List>
  );
}

export function useFilterApplicationRating(job?: IJob): FilterConfig<ExtraProps> {
  const { t, userStore } = useStore();

  const users = job ? job.combinedFollowers : userStore.list;

  return useMemo(() => {
    const sortedUsers = sortBy(
      users.map((user: any) => ({
        value: user.id,
        label: user.name,
      })),
      'name',
      'asc',
    );

    return {
      category: 'rating',
      label: t('applicants.filters.rating.title'),
      icon: <IconRatingGood />,
      extraProps: {
        users: sortedUsers,
      },
      AppliedFilter: FilterApplicationRatingApplied,
      Menu: FilterApplicationRatingMenu,
      toQueryData: (filter) => {
        switch (filter.comparer) {
          case 'is':
            if (filter.value === 'withoutmine') return { rating__by_me: 0 };
            if (filter.value === 'withmine') return { rating__by_me: 1 };
            return { rating: filter.value };
          case 'any_of':
            return { rating: filter.value };
          case 'not_is':
          case 'none_of':
            return { rating__exclude: filter.value };
          case 'is_rated_by':
            return { rating__user: filter.value };
          case 'not_is_rated_by':
            return { rating__not_user: filter.value };
          default:
            return {};
        }
      },
    };
  }, [users, t]);
}
